import React from 'react';

import {
  ASRProviderReferenceSuiteOutput,
  ELMA365AppModelInput,
  ELMA365SourceParametersSettingsOutput,
  IngestionPipelineOutput,
  IngestionSourceParametersOutput,
  OCRProviderReferenceSuiteOutput,
} from '../../../../../../../gamma-api';
import ELMA365AppFieldsSelector from '../ELMA365AppFieldsSelector';
import ELMA365AppStatusSelector from '../ELMA365AppStatusSelector';
import { isAllowedForParsingField } from '../ELMA365AppFieldsSelector/utils';
import { getTranslateFunc } from '../../../../../../utils/stringUtil';
import { DIVIDER_CLASS_NAME, BLOCK_GROUP_CLASS_NAME, BLOCK_ITEM_CLASS_NAME } from '../const';
import IbSwitch from '../../../../../../components/common/IbSwitch';
import IbTypography from '../../../../../../components/common/IbTypography';
import {
  getSourceAsrOverlays,
  getSourceAsrSettings,
  getSourceOcrOverlays,
  getSourceOcrSettings,
  mapSourceAsrSettings,
  mapSourceOcrSettings,
} from '../../SourceModal/utils';
import RecognizerSelector from '../../../../../../components/RecognizerSelector';

export interface IParametersExtraProps {
  pipeline?: IngestionPipelineOutput;
  parameters: IngestionSourceParametersOutput;
  selectedApp?: ELMA365AppModelInput;
  loading?: boolean;
  onChange?: (parameters: IngestionSourceParametersOutput) => void;
}

const ParametersExtra: React.FC<IParametersExtraProps> = ({ pipeline, parameters, selectedApp, loading, onChange }) => {
  const translate = getTranslateFunc('Source settings: ELMA365:');

  const settings = parameters.settings as ELMA365SourceParametersSettingsOutput & { source_type: 'ELMA365' };
  const withFiles = settings.files.enabled || (settings.feed.enabled && settings.feed.files.enabled);

  const statusItems = selectedApp
    ? selectedApp.settings.status.items.map((item) => ({
        id: item.id,
        code: item.code,
        name: item.name,
        isSelected: settings.status?.selected_item_ids?.includes(item.id) ?? false,
      }))
    : [];

  const appFields = selectedApp
    ? selectedApp.fields.filter(isAllowedForParsingField).map((field) => ({
        code: field.code,
        name: field.view?.name ?? '',
        type: field.type,
        isSelected: settings.selected_field_codes?.includes(field.code) ?? false,
        contextElementField: field,
      }))
    : [];

  const onFilesEnabledChange = (value: string | boolean) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        files: {
          ...settings.files,
          enabled: !!value,
        },
      },
    });
  };

  const onFeedEnabledChange = (value: string | boolean) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        feed: {
          ...settings.feed,
          enabled: !!value,
        },
      },
    });
  };

  const onFeedFilesEnabledChange = (value: string | boolean) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        feed: {
          ...settings.feed,
          files: {
            ...settings.feed.files,
            enabled: !!value,
          },
        },
      },
    });
  };

  const onOcrChange = (ocr: OCRProviderReferenceSuiteOutput) => {
    onChange?.({
      ...parameters,
      extraction: {
        ...parameters.extraction,
        ocr: mapSourceOcrSettings(parameters.extraction.ocr, ocr),
      },
    });
  };

  const onAsrChange = (asr: ASRProviderReferenceSuiteOutput) => {
    onChange?.({
      ...parameters,
      extraction: {
        ...parameters.extraction,
        asr: mapSourceAsrSettings(parameters.extraction.asr, asr),
      },
    });
  };

  const onStatusEnabledChange = (value: string | boolean) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        status: {
          ...settings.status,
          refresh_enabled: !!value,
        },
      },
    });
  };

  const onSelectedStatusChange = (selectedItemIds: number[]) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        status: {
          ...settings.status,
          selected_item_ids: selectedItemIds,
        },
      },
    });
  };

  const onSelectedFieldsChange = (selectedFieldsCodes: string[]) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        selected_field_codes: selectedFieldsCodes,
      },
    });
  };

  return (
    <>
      <div className={BLOCK_GROUP_CLASS_NAME}>
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography>
            <IbSwitch checked={settings.files.enabled} size="small" onChange={onFilesEnabledChange} />
            <IbTypography.Paragraph strong type="secondary">
              {translate('Files: Title')}
            </IbTypography.Paragraph>
          </IbTypography>
          <IbTypography.Paragraph type="secondary">{translate('Files: Description')}</IbTypography.Paragraph>
        </div>
      </div>
      <div className={BLOCK_GROUP_CLASS_NAME}>
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography>
            <IbSwitch checked={settings.feed.enabled} size="small" onChange={onFeedEnabledChange} />
            <IbTypography.Paragraph strong type="secondary">
              {translate('Feed: Title')}
            </IbTypography.Paragraph>
          </IbTypography>
          <IbTypography.Paragraph type="secondary">{translate('Feed: Description')}</IbTypography.Paragraph>
        </div>
        {settings.feed.enabled && (
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <div className={DIVIDER_CLASS_NAME} />
            <IbTypography>
              <IbSwitch checked={settings.feed.files.enabled} size="small" onChange={onFeedFilesEnabledChange} />
              <IbTypography.Paragraph strong type="secondary">
                {translate('Feed: Files: Title')}
              </IbTypography.Paragraph>
            </IbTypography>
            <IbTypography.Paragraph type="secondary">{translate('Feed: Files: Description')}</IbTypography.Paragraph>
          </div>
        )}
      </div>
      {withFiles && (
        <>
          <div className={BLOCK_GROUP_CLASS_NAME}>
            <RecognizerSelector.Ocr
              overlays={getSourceOcrOverlays(pipeline)}
              suite={getSourceOcrSettings(pipeline, parameters.extraction)}
              onChange={onOcrChange}
            />
          </div>
          <div className={BLOCK_GROUP_CLASS_NAME}>
            <RecognizerSelector.Asr
              overlays={getSourceAsrOverlays(pipeline)}
              suite={getSourceAsrSettings(pipeline, parameters.extraction)}
              onChange={onAsrChange}
            />
          </div>
        </>
      )}
      <div className={BLOCK_GROUP_CLASS_NAME}>
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography>
            <IbSwitch checked={settings.status?.refresh_enabled} size="small" onChange={onStatusEnabledChange} />
            <IbTypography.Paragraph strong type="secondary">
              {translate('Status selector: Title')}
            </IbTypography.Paragraph>
          </IbTypography>
          <IbTypography.Paragraph type="secondary">{translate('Status selector: Description')}</IbTypography.Paragraph>
        </div>
        {settings.status?.refresh_enabled && (
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {translate('Status selector: Subtitle')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">{translate('Status selector: Hint')}</IbTypography.Paragraph>
            <ELMA365AppStatusSelector items={statusItems} loading={loading} onChange={onSelectedStatusChange} />
          </div>
        )}
      </div>
      <div className={BLOCK_GROUP_CLASS_NAME}>
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Fields selector: Subtitle')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">{translate('Fields selector: Hint')}</IbTypography.Paragraph>
          <ELMA365AppFieldsSelector fields={appFields} loading={loading} onChange={onSelectedFieldsChange} />
        </div>
      </div>
    </>
  );
};

export default ParametersExtra;
